import { type TFunction } from 'i18next';

import {
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
} from 'widgets/ComposableReportWidget';

interface GetProductOrdersWidgetConfigProps {
    t: TFunction;
    fromMissedSavings: boolean;
    canLoadMoreOrders: boolean;
    loadMoreOrders: () => void;
    onOrderDetailsClick: (payload: ReportWidgetInteractionPayload) => void;
}

export const getProductOrdersWidgetConfig = ({
    t,
    fromMissedSavings,
    canLoadMoreOrders,
    loadMoreOrders,
    onOrderDetailsClick,
}: GetProductOrdersWidgetConfigProps): ReportWidgetConfig =>
    ({
        widgetId: 'savings-summary-product-orders',
        title: t('savingsSummary.productsPurchasesTitle', 'Purchase History'),
        titleVariant: 'section',
        layout: 'stacked',
        onDetailsClick: onOrderDetailsClick,
        visualization: {
            enabled: false,
            type: 'bar',
        },
        pagination: {
            mode: 'server',
            pageSize: 25,
            canLoadMore: canLoadMoreOrders,
            onLoadMore: loadMoreOrders,
        },
        table: {
            enabled: true,
            columns: [
                {
                    id: 'date',
                    label: t('Date'),
                    valueKey: 'date',
                    format: 'text',
                    align: 'left',
                },
                {
                    id: 'order',
                    label: t('Order'),
                    valueKey: 'orderNumber',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    isDetailsTrigger: true,
                },
                {
                    id: 'property',
                    label: t('Property'),
                    valueKey: 'propertyName',
                    format: 'text',
                    align: 'left',
                },
                {
                    id: 'supplier',
                    label: t('Supplier'),
                    valueKey: 'suppliers',
                    format: 'text',
                    align: 'left',
                },
                {
                    id: 'quantity',
                    label: t('Quantity'),
                    valueKey: 'quantity',
                    format: 'number',
                    align: 'right',
                },
                {
                    id: 'avgSupplierPrice',
                    label: fromMissedSavings
                        ? t(
                              'savingsSummary.missedSavingsColumnBestPrice',
                              'Best Price',
                          )
                        : t('Avg Supplier Price'),
                    valueKey: 'avgSupplierPrice',
                    format: 'currency',
                    align: 'right',
                },
                {
                    id: 'pricePaid',
                    label: t('Price Paid'),
                    valueKey: 'pricePaid',
                    format: 'currency',
                    align: 'right',
                },
                {
                    id: 'savings',
                    label: fromMissedSavings
                        ? t('savingsSummary.missedSavings', 'Missed Savings')
                        : t('Savings'),
                    valueKey: 'savings',
                    format: 'currency',
                    align: 'right',
                    highlightColor: fromMissedSavings
                        ? 'metrics.warning'
                        : 'metrics.positive',
                },
                ...(fromMissedSavings
                    ? []
                    : [
                          {
                              id: 'percentSaved',
                              label: t(
                                  'savingsSummary.productsColumnPercentSaved',
                              ),
                              valueKey: 'percentSaved',
                              format: 'percent',
                              align: 'right',
                              highlightColor: 'metrics.positive',
                          },
                      ]),
            ],
            options: {
                emptyMessage: t(
                    'savingsSummary.productsPurchasesEmpty',
                    'No purchases found for this product',
                ),
            },
        },
    } as ReportWidgetConfig);
